import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import SendIcon from '@mui/icons-material/Send';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { ChatMessage } from '../../../../models';
import { ChatbotService } from '../../../../services';
import { fromSubscription } from '../../../../store';
import { useQuery } from '../../../../routes';
import { ConsentBar } from './ConsentBar';
import { CHATBOT_SUGGESTED_QUESTIONS } from './constants';
import { KatalonLogo } from '../../../../components';

const useStyles = makeStyles(theme => ({
  floatingBtn: {
    position: 'fixed',
    bottom: theme.spacing(4),
    right: theme.spacing(4),
    width: 56,
    height: 56,
    borderRadius: '50%',
    backgroundColor: '#5959EB',
    color: 'white',
    zIndex: 1300,
    boxShadow: '0 4px 12px rgba(89, 89, 235, 0.4)',
    '&:hover': {
      backgroundColor: '#4343C8',
    },
  },
  chatPanel: {
    position: 'fixed',
    bottom: theme.spacing(12),
    right: theme.spacing(4),
    width: 400,
    height: 600,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: theme.spacing(1.5),
    overflow: 'hidden',
    zIndex: 1300,
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)',
    border: '1px solid rgba(89, 89, 235, 0.15)',
  },
  chatHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#5959EB',
    padding: theme.spacing(1.5, 2),
    color: 'white',
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: 16,
    color: 'white',
  },
  closeBtn: {
    color: 'white',
    padding: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
    },
  },
  messagesArea: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    backgroundColor: '#FFFFFF',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    minHeight: 420,
    maxHeight: 600,
  },
  botMessageRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  botAvatar: {
    flexShrink: 0,
  },
  botBubble: {
    backgroundColor: '#F4F4F8',
    borderRadius: theme.spacing(0.5, 1.5, 1.5, 1.5),
    padding: theme.spacing(1, 1.5),
    maxWidth: '80%',
  },
  botBubbleText: {
    fontSize: 14,
    color: '#333',
    lineHeight: 1.5,
    whiteSpace: 'pre-line',
  },
  userMessageRow: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  userBubble: {
    backgroundColor: '#5959EB',
    borderRadius: theme.spacing(1.5, 0.5, 1.5, 1.5),
    padding: theme.spacing(1, 1.5),
    maxWidth: '80%',
  },
  userBubbleText: {
    fontSize: 14,
    color: 'white',
    lineHeight: 1.5,
  },
  suggestedQuestions: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  suggestedBtn: {
    justifyContent: 'flex-start',
    textAlign: 'left',
    textTransform: 'none',
    borderColor: '#DCDFE6',
    color: '#333',
    fontSize: 13,
    padding: theme.spacing(0.75, 1.5),
    borderRadius: theme.spacing(1.5),
    fontWeight: 400,
    '&:hover': {
      borderColor: '#5959EB',
      backgroundColor: 'rgba(89, 89, 235, 0.05)',
    },
  },
  inputArea: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 1.5),
    borderTop: '1px solid #EDEDF5',
    backgroundColor: '#FFFFFF',
    flexShrink: 0,
    gap: theme.spacing(1),
  },
  inputBase: {
    flex: 1,
    fontSize: 14,
    backgroundColor: '#F4F4F8',
    borderRadius: theme.spacing(3),
    padding: theme.spacing(0.75, 2),
    '& input': {
      padding: 0,
    },
  },
  sendBtn: {
    color: '#5959EB',
    padding: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: 'rgba(89, 89, 235, 0.08)',
    },
    '&.Mui-disabled': {
      color: '#BDBDBD',
    },
  },
  loadingRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  loadingBubble: {
    backgroundColor: '#F4F4F8',
    borderRadius: theme.spacing(0.5, 1.5, 1.5, 1.5),
    padding: theme.spacing(1, 1.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
}));

interface DisplayMessage {
  id: string;
  role: 'user' | 'assistant' | 'error';
  content: string;
}

const CHATBOT_CONSENT_STORAGE_KEY = 'katalon-chatbot-consent-given';

const getStoredConsent = (): boolean => {
  try {
    return window.localStorage.getItem(CHATBOT_CONSENT_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const setStoredConsent = (value: boolean): void => {
  try {
    window.localStorage.setItem(CHATBOT_CONSENT_STORAGE_KEY, String(value));
  } catch {
    // Ignore storage errors so chatbot still works when storage is unavailable.
  }
};

export const AIChatbot = () => {
  const classes = useStyles();
  const intl = useIntl();
  const { get } = useQuery();
  const accountId = Number(get('accountId'));
  const subscriptions = useSelector(fromSubscription.selectByAccountId(accountId));

  const [open, setOpen] = useState(false);
  const [consentGiven, setConsentGiven] = useState(getStoredConsent);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [displayMessages, setDisplayMessages] = useState<DisplayMessage[]>([
    { id: 'init-1', role: 'assistant', content: intl.formatMessage({ id: 'chatbot.welcome.message.1' }) },
    { id: 'init-2', role: 'assistant', content: intl.formatMessage({ id: 'chatbot.welcome.message.2' }) },
  ]);
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const sessionIdRef = useRef<string>(uuidv4());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const suggestionsUsedRef = useRef(false);

  const userPlan = useMemo(
    () => subscriptions.map(sub => sub.feature).join(',').toLowerCase(),
    [subscriptions],
  );

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [displayMessages, scrollToBottom, open]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleConsentAgree = () => {
    setStoredConsent(true);
    setConsentGiven(true);
    setOpen(true);
  };

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || isLoading) return;

    const userMessage: ChatMessage = { role: 'user', content: text };
    const updatedHistory = [...chatHistory, userMessage];

    setDisplayMessages(prev => [...prev, { id: uuidv4(), role: 'user', content: text }]);
    setChatHistory(updatedHistory);
    setInputValue('');
    setIsLoading(true);
    suggestionsUsedRef.current = true;

    try {
      const response = await ChatbotService.sendChatMessage({
        messages: updatedHistory,
        context: {
          page_url: window.location.href,
          user_plan: userPlan,
          session_id: sessionIdRef.current,
        },
      });

      const assistantMessage: ChatMessage = { role: 'assistant', content: response.answer };
      setChatHistory(prev => [...prev, assistantMessage]);
      setDisplayMessages(prev => [
        ...prev,
        { id: uuidv4(), role: 'assistant', content: response.answer },
      ]);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Chatbot API error:', error);
      setDisplayMessages(prev => [
        ...prev,
        { id: uuidv4(), role: 'error', content: intl.formatMessage({ id: 'chatbot.error.message' }) },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [chatHistory, isLoading, userPlan, intl]);

  const handleSend = () => {
    sendMessage(inputValue);
  };

  const handleSuggestedQuestion = (questionKey: string) => {
    const question = intl.formatMessage({ id: questionKey });
    sendMessage(question);
  };

  const showSuggestions = !suggestionsUsedRef.current && displayMessages.length <= 2;

  return (
    <>
      {/* Chat Panel */}
      {open && (
        <Paper className={classes.chatPanel} elevation={0}>
          {/* Header */}
          <Box className={classes.chatHeader}>
            <Box className={classes.headerLeft}>
              <Box className={classes.botAvatar}>
                <KatalonLogo />
              </Box>
              <Typography className={classes.headerTitle}>
                <FormattedMessage id="chatbot.header.title" />
              </Typography>
            </Box>
            <IconButton className={classes.closeBtn} onClick={handleClose} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Messages Area */}
          <Box className={classes.messagesArea}>
            {displayMessages.map(msg => {
              if (msg.role === 'user') {
                return (
                  <Box key={msg.id} className={classes.userMessageRow}>
                    <Box className={classes.userBubble}>
                      <Typography className={classes.userBubbleText}>{msg.content}</Typography>
                    </Box>
                  </Box>
                );
              }
              return (
                <Box key={msg.id} className={classes.botMessageRow}>
                  <Box className={classes.botAvatar}>
                    <KatalonLogo />
                  </Box>
                  <Box className={classes.botBubble}>
                    <Typography className={classes.botBubbleText}>{msg.content}</Typography>
                  </Box>
                </Box>
              );
            })}

            {/* Suggested Questions */}
            {showSuggestions && (
              <Box className={classes.suggestedQuestions}>
                {CHATBOT_SUGGESTED_QUESTIONS.map(key => (
                  <Button
                    key={key}
                    variant="outlined"
                    className={classes.suggestedBtn}
                    onClick={() => handleSuggestedQuestion(key)}
                  >
                    <FormattedMessage id={key} />
                  </Button>
                ))}
              </Box>
            )}

            {/* Loading indicator */}
            {isLoading && (
              <Box className={classes.loadingRow}>
                <Box className={classes.botAvatar}>
                  <KatalonLogo />
                </Box>
                <Box className={classes.loadingBubble}>
                  <CircularProgress size={14} sx={{ color: '#5959EB' }} />
                  <Typography fontSize={13} color="#666">
                    <FormattedMessage id="chatbot.typing" />
                  </Typography>
                </Box>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>
          {consentGiven
            ? (
              <Box className={classes.inputArea}>
                <InputBase
                  className={classes.inputBase}
                  placeholder={intl.formatMessage({ id: 'chatbot.input.placeholder' })}
                  value={inputValue}
                  onChange={e => setInputValue(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  multiline
                  maxRows={3}
                />
                <IconButton
                  className={classes.sendBtn}
                  onClick={handleSend}
                  disabled={!inputValue.trim() || isLoading}
                  size="small"
                >
                  <SendIcon fontSize="small" />
                </IconButton>
              </Box>
            )
            : (
              <ConsentBar
                onAgree={handleConsentAgree}
              />
            )}

        </Paper>
      )}

      {/* Floating Toggle Button */}
      <IconButton
        id="ai-chatbot-toggle-btn"
        className={classes.floatingBtn}
        onClick={open ? handleClose : handleOpen}
      >
        {open ? <CloseIcon /> : <ChatIcon />}
      </IconButton>
    </>
  );
};
